import styled from 'styled-components';
import { Form, Switch, Button, Card, Tabs, Modal } from 'antd';
import { colors, typography, spacing, sizes } from './theme';

// Wspólny Switch z jednolitym kolorem primary
export const StyledSwitch = styled(Switch)`
  &.ant-switch-checked {
    background-color: ${colors.primary};
  }
  &.ant-switch-checked:hover:not(.ant-switch-disabled) {
    background-color: ${colors.primaryHover};
  }
`;

// Wspólny Form z jednolitymi stylami
export const StyledForm = styled(Form)`
  .ant-form-item-label > label {
    font-size: ${typography.label.fontSize};
    font-weight: ${typography.label.fontWeight};
    color: ${typography.label.color};
  }

  .ant-input,
  .ant-input-affix-wrapper,
  .ant-select-selector {
    border-radius: ${sizes.borderRadius} !important;
  }

  .ant-input,
  .ant-input-affix-wrapper {
    padding: 10px 12px;
  }

  .ant-btn-primary {
    border-radius: ${sizes.borderRadius};
    background: ${colors.primary};
    border-color: ${colors.primary};

    &:hover {
      background: ${colors.primaryHover} !important;
      border-color: ${colors.primaryHover} !important;
    }
  }

  .ant-btn-default {
    border-radius: ${sizes.borderRadius};
    }
`;

// Tytuł sekcji - używany w wielu miejscach
export const SectionTitle = styled.h3`
  font-size: ${typography.sectionTitle.fontSize};
  font-weight: ${typography.sectionTitle.fontWeight};
  color: ${typography.sectionTitle.color};
  margin: 0 0 ${spacing.lg} 0;
`;

// Container dla jednego ustawienia z switchem
export const SettingItem = styled.div`
  margin-bottom: ${spacing.xl};
`;

// Wiersz ustawień - container dla switch + tekstu
export const SettingRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.md};
`;

// Zawartość tekstowa obok switcha
export const SettingContent = styled.div`
  flex: 1;
`;

// Label dla ustawienia
export const SettingLabel = styled.div`
  font-size: ${typography.label.fontSize};
  font-weight: ${typography.label.fontWeight};
  color: ${typography.label.color};
`;

// Opis pod labelem
export const SettingDescription = styled.div`
  font-size: ${typography.description.fontSize};
  color: ${typography.description.color};
  margin-top: 4px;
`;

// Primary Button ze spójnymi kolorami
export const PrimaryButton = styled(Button)`
  background-color: ${colors.primary};
  border-color: ${colors.primary};
  &:hover:not(:disabled) {
    background-color: ${colors.primaryHover} !important;
    border-color: ${colors.primaryHover} !important;
  }

  &:focus {
    background-color: ${colors.primary};
    border-color: ${colors.primary};
  }
`;

// Success Button
export const SuccessButton = styled(Button)`
  background-color: ${colors.success};
  border-color: ${colors.success};
  &:hover:not(:disabled) {
    background-color: ${colors.successHover} !important;
    border-color: ${colors.successHover} !important;
  }
`;

// Error/Danger Button
export const DangerButton = styled(Button)`
  background-color: ${colors.error};
  border-color: ${colors.error};
  &:hover:not(:disabled) {
    background-color: ${colors.errorHover} !important;
    border-color: ${colors.errorHover} !important;
  }
`;

// Wspólny Card dla list (CategoryList, MealsList, etc.)
export const CustomCard = styled(Card)`
  width: 100% !important;
  border: 1px solid ${colors.border};
  margin-bottom: ${spacing.xl};
  border-radius: ${sizes.borderRadius};

  && .ant-card-body,
  .ant-card-head {
    padding: ${spacing.sm} !important;
  }

  && .ant-table-tbody > tr > td {
    border-bottom: none;
  }

  && .ant-table-cell {
    padding: ${spacing.sm} ${spacing.sm} ${spacing.sm} 4px !important;
  }
`;

// Wspólne Tabs z jednolitym stylem
export const StyledTabs = styled(Tabs)`
  .ant-tabs-nav::before {
    border-bottom: 1px solid ${colors.border};
  }

  .ant-tabs-tab {
    padding: ${spacing.md} 0;
    font-size: 15px;
    color: ${colors.textSecondary};
    transition: color 0.2s;

    &:hover {
      color: ${colors.textPrimary};
    }
  }

  .ant-tabs-tab-active {
    .ant-tabs-tab-btn {
      color: ${colors.textPrimary};
      font-weight: 500;
    }
  }

  .ant-tabs-ink-bar {
    background: ${colors.textPrimary};
    height: 2px;
  }

  .ant-tabs-content-holder {
    padding: ${spacing.xl} 0;
    overflow: visible !important;
  }

  .ant-tabs-content,
  .ant-tabs-tabpane {
    overflow: visible !important;
  }

  /* Mobile: allow horizontal tab-bar scroll without blocking vertical page scroll */
  @media (max-width: 768px) {
    .ant-tabs-nav-wrap {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    .ant-tabs-nav-list {
      touch-action: pan-x;
    }

    .ant-tabs-content-holder {
      padding: ${spacing.md} 0;
      overflow-x: hidden;
    }
  }
`;

// Pill-style Tabs — do użycia jako subtaby wewnątrz zakładek
export const PillTabs = styled(Tabs)`
  .ant-tabs-nav::before {
    display: none;
  }

  .ant-tabs-nav {
    margin-bottom: ${spacing.xl} !important;
  }

  .ant-tabs-tab {
    padding: 6px 16px;
    font-size: 14px;
    color: ${colors.textSecondary};
    background: transparent;
    border: 1px solid ${colors.border};
    border-radius: 20px;
    margin: 0 8px 0 0 !important;
    transition: all 0.2s;

    &:hover {
      color: ${colors.textPrimary};
      border-color: ${colors.textPrimary};
    }
  }

  .ant-tabs-tab-active {
    background: transparent;
    border-color: ${colors.textPrimary};
    border-width: 2px;

    .ant-tabs-tab-btn {
      color: ${colors.textPrimary} !important;
      font-weight: 600;
    }
  }

  .ant-tabs-ink-bar {
    display: none;
  }

  .ant-tabs-content-holder {
    padding: 0;
  }
`;

// Wspólny Modal
export const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: ${sizes.borderRadius};
  }

  .ant-modal-header {
    border-radius: ${sizes.borderRadius} ${sizes.borderRadius} 0 0;
  }

  .ant-modal-footer {
    .ant-btn {
      border-radius: ${sizes.borderRadius};
      }
  }
`;
